import type { TimelineItemOut } from "@/api";
import { dayKey } from "@/lib/format";

/** One line of the record: an entry or event as it is, or a run of work events folded into one. */
export type TimelineRow =
  | { kind: "item"; item: TimelineItemOut }
  | { kind: "bundle"; key: string; items: TimelineItemOut[] };

/** From three on a run is one line (design 2026-10-24 §6.3); two stand as they are. */
export const BUNDLE_MIN = 3;

/**
 * The work events that may fold (design 2026-10-24 §8.4): plan saves,
 * measures, the closure check, the reading, classification, adoption, the
 * care of contact and customer, the price sync. An allow-list, not a
 * deny-list: everything else — the case created, a stage change, a
 * calculation, an approval or its withdrawal, a handover, cancel and
 * restore, a customer assigned, the grating changed, an entry retracted, and
 * every type the server adds tomorrow — stands on its own line, so the audit
 * trail (E-52) can be read row by row.
 *
 * The server already folds a run of one person's plan saves into one line
 * („{n} Planstände gespeichert.", `register_timeline.py`); this folds what
 * is left between them on top of that.
 */
export const BUNDLED_EVENTS: ReadonlySet<string> = new Set([
  "plan_version_created",
  "measurement_proposed",
  "measurement_confirmed",
  "measurement_corrected",
  "measurement_rejected",
  "closure_checked",
  "extraction_enqueued",
  "extraction_succeeded",
  "document_uploaded",
  "document_classified",
  "correspondence_adopted",
  "case_contact_updated",
  "customer_updated",
  "extraction_form_chosen",
  "price_sync_completed",
]);

/**
 * The run an item may join: one person on one local day — or none. A mail,
 * a note, a file, an event that never folds, and an event nobody is named
 * for (the system's own) have no run and break any around them.
 */
function runOf(item: TimelineItemOut): string | null {
  if (item.kind !== "event") return null;
  const { eventType, actorDisplayName, at } = item.event;
  if (!BUNDLED_EVENTS.has(eventType) || actorDisplayName === null || actorDisplayName === "") return null;
  return `${actorDisplayName}\u0000${dayKey(at)}`;
}

/**
 * A run of `BUNDLE_MIN` or more is one bundle, keyed by its **oldest** event
 * (Task 6 ruling): a run grows at its newest end, and a key taken from there
 * changed with every save — React drew the bundle anew, and one a person had
 * opened folded shut under their eyes.
 */
function flush(rows: TimelineRow[], run: TimelineItemOut[]): void {
  const oldest = run.at(-1);
  if (oldest === undefined) return;
  if (run.length >= BUNDLE_MIN && oldest.kind === "event") {
    rows.push({ kind: "bundle", key: `bundle-${oldest.event.id}`, items: [...run] });
    return;
  }
  for (const item of run) rows.push({ kind: "item", item });
}

/**
 * The rows the record draws, in the items' order (newest first, as the
 * server sends them): runs of **neighbouring** work events of one person on
 * one local day, from `BUNDLE_MIN` on, become one bundle (§8.4), named by
 * `bundleTitle`. Everything else is an item of its own.
 */
export function bundleEvents(items: readonly TimelineItemOut[]): TimelineRow[] {
  const rows: TimelineRow[] = [];
  let run: TimelineItemOut[] = [];
  let current: string | null = null;
  for (const item of items) {
    const key = runOf(item);
    if (key !== null && key === current) {
      run.push(item);
      continue;
    }
    flush(rows, run);
    run = [item];
    current = key;
  }
  flush(rows, run);
  return rows;
}

/** What a run of work did, in four kinds — the verb its title starts with. */
type Kind = "planung" | "unterlagen" | "kunde" | "preise";

const KIND_VERB: Record<Kind, string> = {
  planung: "Planung bearbeitet",
  unterlagen: "Unterlagen bearbeitet",
  kunde: "Kundendaten gepflegt",
  preise: "Preise abgeglichen",
};

/** „3-mal" — for what is done again rather than counted in pieces. */
const times = (n: number, once: string, again: (n: number) => string) => (n === 1 ? once : again(n));

/**
 * How a bundle's title names each type of work it holds (design 2026-10-24
 * §6.3, UX-Gutachten 1, B-18) — one phrase per type, by how many there are,
 * in this order. Keyed by every type `BUNDLED_EVENTS` lets in (a test holds
 * the two together); a type without a phrase leaves the bundle on its
 * newest line, word for word (`bundleTitle`).
 */
export const CATEGORIES: Readonly<Record<string, { kind: Kind; phrase: (n: number) => string }>> = {
  plan_version_created: { kind: "planung", phrase: (n) => (n === 1 ? "1 Stand" : `${n} Stände`) },
  measurement_proposed: { kind: "planung", phrase: (n) => `${n} ${n === 1 ? "Maß" : "Maße"} vorgeschlagen` },
  measurement_corrected: { kind: "planung", phrase: (n) => `${n} ${n === 1 ? "Maß" : "Maße"} korrigiert` },
  measurement_confirmed: { kind: "planung", phrase: (n) => `${n} ${n === 1 ? "Maß" : "Maße"} bestätigt` },
  measurement_rejected: { kind: "planung", phrase: (n) => `${n} ${n === 1 ? "Maß" : "Maße"} verworfen` },
  closure_checked: { kind: "planung", phrase: (n) => times(n, "Schließung geprüft", (k) => `Schließung ${k}-mal geprüft`) },
  document_uploaded: { kind: "unterlagen", phrase: (n) => `${n} ${n === 1 ? "Unterlage" : "Unterlagen"} abgelegt` },
  document_classified: { kind: "unterlagen", phrase: (n) => `${n} ${n === 1 ? "Unterlage" : "Unterlagen"} eingeordnet` },
  correspondence_adopted: { kind: "unterlagen", phrase: (n) => `${n} ${n === 1 ? "Zeichnung" : "Zeichnungen"} übernommen` },
  extraction_enqueued: { kind: "unterlagen", phrase: (n) => times(n, "Auslese angestoßen", (k) => `Auslese ${k}-mal angestoßen`) },
  extraction_succeeded: { kind: "unterlagen", phrase: (n) => times(n, "Auslese abgeschlossen", (k) => `${k} Auslesen abgeschlossen`) },
  extraction_form_chosen: { kind: "unterlagen", phrase: (n) => times(n, "Lesart gewählt", (k) => `Lesart ${k}-mal gewählt`) },
  case_contact_updated: { kind: "kunde", phrase: (n) => times(n, "Ansprechpartner geändert", (k) => `Ansprechpartner ${k}-mal geändert`) },
  customer_updated: { kind: "kunde", phrase: (n) => times(n, "Kundendaten geändert", (k) => `Kundendaten ${k}-mal geändert`) },
  price_sync_completed: { kind: "preise", phrase: (n) => times(n, "Preisspiegel abgeglichen", (k) => `Preisspiegel ${k}-mal abgeglichen`) },
};

/**
 * How many saves one line stands for, from the server's own sentence — the
 * wire carries no count beside it (`register_summary`): a run of one
 * person's saves folded into „{n} Planstände gespeichert." (`plan_saves`),
 * or one save, „Planstand gespeichert." or „Planstand gespeichert: „…“"
 * (`_plan_saved`). Any other shape is `null` (fix round 1): counting it as
 * one would be a guess, and the bundle keeps its newest line instead.
 */
function savesIn(summary: string): number | null {
  const text = summary.trim();
  const folded = /^(\d+) Planstände gespeichert\.$/.exec(text);
  if (folded) return Number(folded[1]);
  return /^Planstand gespeichert(?:\.|: „[\s\S]*“\.?)$/.test(text) ? 1 : null;
}

/**
 * A bundle's title from what it holds (design 2026-10-24 §6.3, UX-Gutachten
 * 1, B-18): a verb for the whole — „Planung bearbeitet", or „Vorgang
 * bearbeitet" where the run did more than one kind of work — then each type
 * with its count, „6 Stände, 2 Maße korrigiert". The head of a bundle named
 * its newest line before, and „5 Planstände gespeichert" stood over a
 * corrected measure and a closure check. `null` where a line is of a type
 * the title cannot name, or a plan save's sentence of a shape it cannot
 * count (`savesIn`): the bundle then shows its newest line word for word
 * (§8.4) rather than a title that leaves something out or guesses.
 */
export function bundleTitle(items: readonly TimelineItemOut[]): string | null {
  const counts = new Map<string, number>();
  const kinds = new Set<Kind>();
  for (const item of items) {
    if (item.kind !== "event") return null;
    const category = CATEGORIES[item.event.eventType];
    if (category === undefined) return null;
    kinds.add(category.kind);
    const pieces = item.event.eventType === "plan_version_created" ? savesIn(item.event.summary) : 1;
    if (pieces === null) return null;
    counts.set(item.event.eventType, (counts.get(item.event.eventType) ?? 0) + pieces);
  }
  const [only] = kinds;
  if (only === undefined) return null;
  const verb = kinds.size === 1 ? KIND_VERB[only] : "Vorgang bearbeitet";
  const parts = Object.entries(CATEGORIES).flatMap(([type, category]) => {
    const n = counts.get(type);
    return n === undefined ? [] : [category.phrase(n)];
  });
  return `${verb} · ${parts.join(", ")}`;
}
